import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { NavLink } from 'react-router-dom';
import {
  CreditCard, UploadCloud, CheckCircle2, XCircle, AlertTriangle,
  Search, RefreshCw, FileSpreadsheet, ArrowLeft, Download, Loader2, Save
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { listStudents, updateStudent } from '../../services/firebase/studentsService';
import { CLASS_OPTIONS } from '../../lib/pdfUtils';
import defaultRfidData from '../../data/defaultStudentRfids.json';
import { toast } from 'sonner';

/**
 * Normalize class name to ERP standard:
 * 'Nursery', 'LKG', 'UKG', '1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th'
 */
function normalizeClassName(raw) {
  if (!raw) return '';
  let str = String(raw).trim().toUpperCase();
  // Remove words like "CLASS", "GRADE", "STD"
  str = str.replace(/^(CLASS|GRADE|STD)\s*/i, '').trim();
  // Remove section suffix if any (e.g. "10 A" -> "10")
  str = str.replace(/\s+[A-Z]$/i, '').trim();

  const map = {
    '1': '1st', '01': '1st', '1ST': '1st',
    '2': '2nd', '02': '2nd', '2ND': '2nd',
    '3': '3rd', '03': '3rd', '3RD': '3rd',
    '4': '4th', '04': '4th', '4TH': '4th',
    '5': '5th', '05': '5th', '5TH': '5th',
    '6': '6th', '06': '6th', '6TH': '6th',
    '7': '7th', '07': '7th', '7TH': '7th',
    '8': '8th', '08': '8th', '8TH': '8th',
    '9': '9th', '09': '9th', '9TH': '9th',
    '10': '10th', '10TH': '10th',
    'NURSERY': 'Nursery', 'NURS': 'Nursery',
    'LKG': 'LKG', 'L.K.G': 'LKG', 'L.K.G.': 'LKG',
    'UKG': 'UKG', 'U.K.G': 'UKG', 'U.K.G.': 'UKG',
  };

  return map[str] || str;
}

/**
 * Clean student name for string comparison
 */
function cleanName(str) {
  return String(str || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Similarity score between two student names (0.0 to 1.0)
 * Handles initials, surname placement, and minor typing variations.
 */
function computeNameScore(nameA, nameB) {
  const a = cleanName(nameA);
  const b = cleanName(nameB);
  if (!a || !b) return 0;
  if (a === b) return 1.0;

  const wordsA = a.split(' ').filter(Boolean);
  const wordsB = b.split(' ').filter(Boolean);

  let exactWordMatches = 0;
  let partialMatches = 0;

  const usedB = new Set();

  for (const wA of wordsA) {
    let matched = false;
    for (let i = 0; i < wordsB.length; i++) {
      if (usedB.has(i)) continue;
      const wB = wordsB[i];

      if (wA === wB) {
        exactWordMatches++;
        usedB.add(i);
        matched = true;
        break;
      }
    }

    if (!matched && wA.length > 2) {
      for (let i = 0; i < wordsB.length; i++) {
        if (usedB.has(i)) continue;
        const wB = wordsB[i];
        if (wB.startsWith(wA) || wA.startsWith(wB)) {
          partialMatches += 0.7;
          usedB.add(i);
          break;
        }
      }
    }
  }

  const totalScore = exactWordMatches + partialMatches;
  const maxWords = Math.max(wordsA.length, wordsB.length);
  return Math.min(1.0, totalScore / maxWords);
}

export default function StudentRfidImport() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  const [rawRows, setRawRows] = useState([]);
  const [fileName, setFileName] = useState('');
  const [filterClass, setFilterClass] = useState('ALL');
  const [filterTab, setFilterTab] = useState('ALL'); // 'ALL' | 'READY' | 'ALREADY_SET' | 'UNMATCHED' | 'NO_RFID'
  const [searchQuery, setSearchQuery] = useState('');

  const [manualOverrides, setManualOverrides] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState(0);

  // Load active students from database
  const loadStudents = async () => {
    setLoading(true);
    try {
      const data = await listStudents({ status: 'ACTIVE' });
      setStudents(data);
    } catch (e) {
      toast.error('Failed to load students');
    }
    setLoading(false);
  };

  useEffect(() => {
    loadStudents();
  }, []);

  // Parse an uploaded Excel or CSV file
  const handleFileUpload = (file) => {
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });

        let rows = [];

        // Check if "All Students" sheet exists
        if (workbook.SheetNames.includes('All Students')) {
          rows = XLSX.utils.sheet_to_json(workbook.Sheets['All Students']);
        } else {
          // Aggregate from class sheets
          workbook.SheetNames.forEach((sheetName) => {
            if (sheetName.toLowerCase() === 'summary' || sheetName.toLowerCase() === 'notes') return;
            const sheetRows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName]);
            const inferredClass = normalizeClassName(sheetName);
            sheetRows.forEach((r) => {
              rows.push({
                ...r,
                Class: r.Class || r.class || inferredClass,
              });
            });
          });
        }

        if (rows.length === 0) {
          toast.error('No data found in the uploaded file');
          return;
        }

        setRawRows(rows);
        setManualOverrides({});
        toast.success(`Loaded ${rows.length} records from ${file.name}`);
      } catch (err) {
        console.error('Error parsing file:', err);
        toast.error('Error reading spreadsheet file.');
      }
    };

    reader.readAsArrayBuffer(file);
  };

  // Quick load of the bundled Sep 4 dataset
  const handleLoadDefault = () => {
    setFileName('student_rfid_classwise_updated_sep4.xlsx');
    setRawRows(defaultRfidData);
    setManualOverrides({});
    toast.success(`Loaded ${defaultRfidData.length} records from Sep 4 RFID register`);
  };

  // Process rows and match against database students
  const processedMatches = useMemo(() => {
    if (!rawRows.length || !students.length) return [];

    return rawRows.map((row, index) => {
      const rowId = `row_${index}`;

      // Extract raw fields
      const excelName = String(
        row['Student Name'] || row['student_name'] || row['CHILD NAME'] || row['Name'] || ''
      ).trim();

      const rawClass = row['Class'] || row['class'] || row['CLASS'] || '';
      const normalizedClass = normalizeClassName(rawClass);

      const rawRfid = row['RFID'] !== undefined && row['RFID'] !== null ? String(row['RFID']).trim() : '';
      const cleanRfid = rawRfid.replace(/^0+/, '');

      // Check if user manually selected a student override
      const manualStudentId = manualOverrides[rowId];
      let matchedStudent = null;
      let score = 0;

      if (manualStudentId) {
        matchedStudent = students.find((s) => s.id === manualStudentId) || null;
        score = 1.0;
      } else if (excelName) {
        // Pool of students in the same class
        const classPool = normalizedClass
          ? students.filter((s) => s.className?.toLowerCase() === normalizedClass.toLowerCase())
          : students;

        let bestStudent = null;
        let highestScore = 0;

        // Try exact match first
        for (const s of classPool) {
          const sScore = computeNameScore(excelName, s.fullName);
          if (sScore > highestScore) {
            highestScore = sScore;
            bestStudent = s;
            if (highestScore === 1.0) break;
          }
        }

        // If score is satisfactory (> 0.45), accept match
        if (bestStudent && highestScore >= 0.45) {
          matchedStudent = bestStudent;
          score = highestScore;
        }
      }

      // Determine match status
      let status = 'ready';
      if (!cleanRfid) {
        status = 'no_rfid';
      } else if (!matchedStudent) {
        status = 'unmatched';
      } else {
        const currRfid1 = String(matchedStudent.rfid || '').trim().replace(/^0+/, '');
        const currRfid2 = String(matchedStudent.rfidNo || '').trim().replace(/^0+/, '');
        if (currRfid1 === cleanRfid || currRfid2 === cleanRfid) {
          status = 'already_set';
        } else {
          status = 'ready';
        }
      }

      return {
        rowId,
        excelName,
        rawClass,
        normalizedClass,
        rfid: cleanRfid,
        matchedStudent,
        confidence: score,
        status, // 'ready' | 'already_set' | 'unmatched' | 'no_rfid'
      };
    });
  }, [rawRows, students, manualOverrides]);

  // Filtered rows for display
  const filteredRows = useMemo(() => {
    return processedMatches.filter((item) => {
      // Class filter
      if (filterClass !== 'ALL' && item.normalizedClass !== filterClass) return false;

      // Status tab filter
      if (filterTab === 'READY' && item.status !== 'ready') return false;
      if (filterTab === 'ALREADY_SET' && item.status !== 'already_set') return false;
      if (filterTab === 'UNMATCHED' && item.status !== 'unmatched') return false;
      if (filterTab === 'NO_RFID' && item.status !== 'no_rfid') return false;

      // Search query filter
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const inExcelName = item.excelName.toLowerCase().includes(q);
        const inRfid = item.rfid.includes(q);
        const inMatched = item.matchedStudent?.fullName?.toLowerCase().includes(q) ||
                          item.matchedStudent?.admissionNo?.toLowerCase().includes(q);
        if (!inExcelName && !inRfid && !inMatched) return false;
      }

      return true;
    });
  }, [processedMatches, filterClass, filterTab, searchQuery]);

  // Statistics counters
  const stats = useMemo(() => {
    let ready = 0;
    let alreadySet = 0;
    let unmatched = 0;
    let noRfid = 0;

    processedMatches.forEach((item) => {
      if (item.status === 'ready') ready++;
      else if (item.status === 'already_set') alreadySet++;
      else if (item.status === 'unmatched') unmatched++;
      else if (item.status === 'no_rfid') noRfid++;
    });

    return {
      total: processedMatches.length,
      ready,
      alreadySet,
      unmatched,
      noRfid,
    };
  }, [processedMatches]);

  // Handle manual override of student match
  const handleManualOverride = (rowId, newStudentId) => {
    setManualOverrides((prev) => ({
      ...prev,
      [rowId]: newStudentId,
    }));
  };

  // Save updates to Firestore
  const handleSaveUpdates = async () => {
    const readyItems = processedMatches.filter((item) => item.status === 'ready' && item.matchedStudent && item.rfid);

    if (readyItems.length === 0) {
      toast.error('No students ready for RFID update.');
      return;
    }

    if (!window.confirm(`Update RFID numbers for ${readyItems.length} students in the database?`)) {
      return;
    }

    setSaving(true);
    setSaveProgress(0);

    let updatedCount = 0;
    let failedCount = 0;

    for (let i = 0; i < readyItems.length; i++) {
      const item = readyItems[i];
      try {
        await updateStudent(item.matchedStudent.id, {
          rfid: item.rfid,
          rfidNo: item.rfid,
        });
        updatedCount++;
      } catch (err) {
        console.error('Update error:', err);
        failedCount++;
      }
      setSaveProgress(Math.round(((i + 1) / readyItems.length) * 100));
    }

    setSaving(false);
    toast.success(`Successfully updated ${updatedCount} students with their RFID numbers!`);
    if (failedCount > 0) {
      toast.error(`Failed to update ${failedCount} students.`);
    }

    // Reload students to refresh states
    loadStudents();
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-24" data-testid="student-rfid-import">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <NavLink to="/dashboard/students/directory" className="label-eyebrow text-primary flex items-center gap-1.5 mb-2">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Student Directory
          </NavLink>
          <h1 className="font-display font-black text-3xl tracking-tighter uppercase flex items-center gap-3">
            <CreditCard className="h-7 w-7 text-indigo-500" />
            Student RFID Import & Mapping
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Import student RFID tags from Excel/CSV and link them directly to student profiles in Firestore.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <a
            href="/student_rfid_classwise_updated_sep4.xlsx"
            download="student_rfid_classwise_updated_sep4.xlsx"
            className="h-10 px-4 rounded-2xl bg-secondary text-secondary-foreground text-xs font-bold flex items-center gap-2 hover:bg-secondary/80"
          >
            <Download className="h-3.5 w-3.5" /> Download Template
          </a>
          <button
            onClick={loadStudents}
            disabled={loading}
            className="h-10 w-10 rounded-2xl bg-muted grid place-items-center hover:bg-muted/80"
            title="Refresh database students"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* File Upload / Preload Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <label className="md:col-span-2 block glass-morphism rounded-[2rem] p-6 border-2 border-dashed border-border cursor-pointer text-center hover:border-primary/50 transition-colors">
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={(e) => handleFileUpload(e.target.files?.[0])}
            className="hidden"
          />
          <UploadCloud className="h-9 w-9 mx-auto text-primary" />
          <div className="mt-2 font-bold text-sm">
            {fileName ? `Loaded: ${fileName}` : 'Click to Upload Excel (.xlsx) or CSV'}
          </div>
          <div className="label-eyebrow text-muted-foreground mt-1">
            Accepts columns: Student Name, Class, RFID
          </div>
        </label>

        <div className="glass-morphism rounded-[2rem] p-6 flex flex-col justify-between">
          <div>
            <div className="font-bold text-sm flex items-center gap-2">
              <FileSpreadsheet className="h-4 w-4 text-emerald-500" />
              September 4 Register
            </div>
            <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
              Quickly load all 651 student RFIDs from the pre-configured school register.
            </p>
          </div>
          <button
            onClick={handleLoadDefault}
            className="mt-4 h-10 px-4 rounded-xl bg-primary/10 text-primary hover:bg-primary/20 text-xs font-bold flex items-center justify-center gap-2 transition-colors"
          >
            <FileSpreadsheet className="h-3.5 w-3.5" /> Load Sep 4 Register
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      {processedMatches.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <div className="glass-morphism rounded-2xl p-4">
            <div className="label-eyebrow text-muted-foreground">Total Rows</div>
            <div className="font-display font-black text-2xl tracking-tighter mt-1">{stats.total}</div>
          </div>
          <div className="glass-morphism rounded-2xl p-4 border border-emerald-500/20 bg-emerald-500/5">
            <div className="label-eyebrow text-emerald-500">Ready to Update</div>
            <div className="font-display font-black text-2xl tracking-tighter mt-1 text-emerald-500">{stats.ready}</div>
          </div>
          <div className="glass-morphism rounded-2xl p-4 border border-blue-500/20 bg-blue-500/5">
            <div className="label-eyebrow text-blue-500">Already Up-to-date</div>
            <div className="font-display font-black text-2xl tracking-tighter mt-1 text-blue-500">{stats.alreadySet}</div>
          </div>
          <div className="glass-morphism rounded-2xl p-4 border border-rose-500/20 bg-rose-500/5">
            <div className="label-eyebrow text-rose-500">Unmatched</div>
            <div className="font-display font-black text-2xl tracking-tighter mt-1 text-rose-500">{stats.unmatched}</div>
          </div>
          <div className="glass-morphism rounded-2xl p-4 border border-amber-500/20 bg-amber-500/5">
            <div className="label-eyebrow text-amber-500">Blank RFID</div>
            <div className="font-display font-black text-2xl tracking-tighter mt-1 text-amber-500">{stats.noRfid}</div>
          </div>
        </div>
      )}

      {/* Controls & Filter Bar */}
      {processedMatches.length > 0 && (
        <div className="glass-morphism rounded-[2rem] p-4 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            {/* Status Tabs */}
            <div className="flex bg-muted rounded-full p-1 gap-1 overflow-x-auto thin-scrollbar">
              {[
                { k: 'ALL', label: `All (${stats.total})` },
                { k: 'READY', label: `Ready (${stats.ready})` },
                { k: 'ALREADY_SET', label: `Up-to-date (${stats.alreadySet})` },
                { k: 'UNMATCHED', label: `Unmatched (${stats.unmatched})` },
                { k: 'NO_RFID', label: `No RFID (${stats.noRfid})` },
              ].map((tab) => (
                <button
                  key={tab.k}
                  onClick={() => setFilterTab(tab.k)}
                  className={`px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${
                    filterTab === tab.k ? 'bg-background shadow text-foreground' : 'text-muted-foreground'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Save Button */}
            <button
              onClick={handleSaveUpdates}
              disabled={saving || stats.ready === 0}
              className="h-10 px-5 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold flex items-center gap-2 disabled:opacity-50 transition-all shadow-md"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              {saving ? `Updating ${saveProgress}%...` : `Save & Update ${stats.ready} RFIDs`}
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div className="flex items-center gap-2 px-3 h-10 rounded-2xl border border-border bg-card">
              <Search className="h-3.5 w-3.5 text-muted-foreground" />
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by student name or RFID..."
                className="bg-transparent outline-none text-xs flex-1"
              />
            </div>

            <select
              value={filterClass}
              onChange={(e) => setFilterClass(e.target.value)}
              className="h-10 px-3 rounded-2xl border border-border bg-card text-xs font-bold"
            >
              <option value="ALL">All Classes</option>
              {CLASS_OPTIONS.map((c) => (
                <option key={c} value={c}>Class {c}</option>
              ))}
            </select>

            <div className="text-xs text-muted-foreground flex items-center justify-end px-2">
              Showing {filteredRows.length} of {processedMatches.length} rows
            </div>
          </div>
        </div>
      )}

      {/* Review Table */}
      {processedMatches.length > 0 && (
        <div className="glass-morphism rounded-[2rem] p-4 overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-border text-muted-foreground label-eyebrow">
                <th className="py-2.5 px-3">Class</th>
                <th className="py-2.5 px-3">Excel Student Name</th>
                <th className="py-2.5 px-3">Matched Student (ERP)</th>
                <th className="py-2.5 px-3">RFID to Assign</th>
                <th className="py-2.5 px-3">Current RFID</th>
                <th className="py-2.5 px-3">Status</th>
                <th className="py-2.5 px-3 text-right">Manual Override</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/50">
              {filteredRows.slice(0, 100).map((item) => {
                const classPool = students.filter(
                  (s) => s.className?.toLowerCase() === item.normalizedClass.toLowerCase()
                );

                return (
                  <tr key={item.rowId} className="hover:bg-muted/30 transition-colors">
                    <td className="py-2.5 px-3 font-bold">
                      {item.normalizedClass || item.rawClass || '—'}
                    </td>
                    <td className="py-2.5 px-3 font-semibold">
                      {item.excelName}
                    </td>
                    <td className="py-2.5 px-3">
                      {item.matchedStudent ? (
                        <div>
                          <div className="font-bold text-foreground">{item.matchedStudent.fullName}</div>
                          <div className="text-[10px] text-muted-foreground font-mono">
                            {item.matchedStudent.admissionNo} · Sec {item.matchedStudent.section || 'A'}
                          </div>
                        </div>
                      ) : (
                        <span className="text-rose-500 font-semibold italic">Not Found</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3">
                      {item.rfid ? (
                        <span className="font-mono font-bold bg-muted px-2 py-0.5 rounded-md">
                          {item.rfid}
                        </span>
                      ) : (
                        <span className="text-muted-foreground italic">None</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3 font-mono text-muted-foreground">
                      {item.matchedStudent?.rfid || item.matchedStudent?.rfidNo || '—'}
                    </td>
                    <td className="py-2.5 px-3">
                      {item.status === 'ready' && (
                        <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 font-bold label-eyebrow">
                          Ready
                        </span>
                      )}
                      {item.status === 'already_set' && (
                        <span className="px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-600 font-bold label-eyebrow">
                          Up-to-Date
                        </span>
                      )}
                      {item.status === 'unmatched' && (
                        <span className="px-2 py-0.5 rounded-full bg-rose-500/10 text-rose-600 font-bold label-eyebrow">
                          Unmatched
                        </span>
                      )}
                      {item.status === 'no_rfid' && (
                        <span className="px-2 py-0.5 rounded-full bg-gray-500/10 text-gray-500 font-bold label-eyebrow">
                          No RFID
                        </span>
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-right">
                      <select
                        value={item.matchedStudent?.id || ''}
                        onChange={(e) => handleManualOverride(item.rowId, e.target.value)}
                        className="h-8 px-2 rounded-lg border border-border bg-card text-[11px] max-w-[180px] truncate"
                      >
                        <option value="">(Select student)</option>
                        {classPool.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.fullName} ({s.admissionNo})
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })}

              {filteredRows.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-muted-foreground">
                    No student records match the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {filteredRows.length > 100 && (
            <div className="py-3 text-center text-xs text-muted-foreground border-t border-border mt-2">
              Showing first 100 of {filteredRows.length} matching rows. Use search or class filter to refine.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
